import React, { useEffect, useState } from "react";
import { Trophy, Crown, Medal, Coins, Cat, Sparkles, Award } from "lucide-react";
import api from "../lib/api";
import { SEASONS, SEASON_ORDER } from "../data/season";
import { useApp } from "../context/AppState";

const rankStyle = (rank) => {
  if (rank === 1) return { chip: "bg-[#fff4d6] text-[#b8860b]", ring: "border-[#f0c24b]", icon: Crown };
  if (rank === 2) return { chip: "bg-[#eef1f4] text-[#8794a3]", ring: "border-[#c3ccd6]", icon: Medal };
  if (rank === 3) return { chip: "bg-[#f7e6d8] text-[#b07a4d]", ring: "border-[#e0b591]", icon: Medal };
  return { chip: "bg-[#f5f1eb] text-[#9a8f86]", ring: "border-[#efeae4]", icon: null };
};

function BadgeRow({ badges }) {
  return (
    <div className="flex items-center gap-1">
      {SEASON_ORDER.map((id) => {
        const s = SEASONS[id];
        const has = (badges || []).includes(id);
        return (
          <span key={id} title={s.label}
            className={`grid h-6 w-6 place-items-center rounded-full text-[13px] transition ${has ? "" : "opacity-25 grayscale"}`}
            style={has ? { background: `${s.badge}22` } : {}}>
            {s.emoji}
          </span>
        );
      })}
    </div>
  );
}

export default function Peringkat() {
  const { state } = useApp();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/leaderboard");
        if (alive) setRows(data.leaderboard || []);
      } catch {
        if (alive) setError(true);
      }
    })();
    return () => { alive = false; };
  }, [state?.seasonBadges?.length, state?.points]);

  const you = (rows || []).find((r) => r.isYou);
  const totalSeasons = SEASON_ORDER.length;

  return (
    <div className="kk-reveal">
      {/* header banner */}
      <section className="relative overflow-hidden rounded-3xl kk-coral-gradient p-6 text-white sm:p-8" data-testid="peringkat-banner">
        <div className="absolute -right-8 -top-10 h-44 w-44 rounded-full bg-white/10" />
        <div className="absolute -bottom-12 right-16 h-32 w-32 rounded-full bg-white/10" />
        <div className="relative">
          <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-widest text-white/85">
            <Trophy className="h-4 w-4" /> Papan peringkat musim
          </div>
          <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl">Kejar lencana bareng!</h1>
          <p className="mt-2 max-w-md text-[14px] text-white/85">
            Rawat Mochi di tiap musim untuk mengumpulkan lencana. Pasangan dengan koleksi lencana terbanyak naik ke puncak.
          </p>
          {you && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-[13px] font-bold text-white backdrop-blur" data-testid="your-rank">
              <Sparkles className="h-4 w-4" /> Peringkat kalian: #{you.rank} · {you.badgeCount}/{totalSeasons} lencana
            </div>
          )}
        </div>
      </section>

      <h2 className="mb-4 mt-8 flex items-center gap-2 text-2xl font-extrabold tracking-tight">
        <Award className="h-6 w-6 text-[#e0a94b]" /> Peringkat pasangan
      </h2>

      {error ? (
        <div className="rounded-3xl border border-dashed border-[#e5d9ce] bg-[#fdfbf7] px-6 py-14 text-center text-[14px] text-[#8a8278]">
          Gagal memuat papan peringkat. Coba muat ulang halaman.
        </div>
      ) : !rows ? (
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[76px] animate-pulse rounded-3xl bg-[#f1ece6]" />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const rs = rankStyle(r.rank);
            const Icon = rs.icon;
            const initials = (r.coupleName || "K").split("&").map((s) => s.trim()[0]).filter(Boolean).slice(0, 2).join("");
            return (
              <div key={r.rank}
                data-testid={`leader-row-${r.rank}`}
                className={`flex items-center gap-3 rounded-3xl border bg-white p-4 transition ${r.isYou ? "border-[#ff5a5f] ring-2 ring-[#ffd9d9]" : "border-[#efeae4]"}`}>
                <span className={`grid h-10 w-10 flex-none place-items-center rounded-2xl text-[15px] font-extrabold ${rs.chip}`}>
                  {Icon ? <Icon className="h-5 w-5" /> : r.rank}
                </span>
                <span className={`grid h-11 w-11 flex-none place-items-center rounded-full border-2 bg-[#ffe1dd] text-[13px] font-extrabold text-[#c9566b] ${rs.ring}`}>
                  {initials || "K"}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-[15px] font-extrabold">{r.coupleName}</span>
                    {r.isYou && <span className="flex-none rounded-full bg-[#ffebeb] px-2 py-0.5 text-[10px] font-bold text-[#ff5a5f]">Kalian</span>}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[#8a8278]">
                    <span className="flex items-center gap-1"><Cat className="h-3.5 w-3.5 text-[#c9878f]" /> Lv {r.level}</span>
                    <span className="flex items-center gap-1"><Coins className="h-3.5 w-3.5 text-[#e0a94b]" /> {Number(r.points).toLocaleString("id-ID")}</span>
                  </div>
                </div>
                <div className="flex flex-none flex-col items-end gap-1">
                  <span className="rounded-full bg-[#fffaf0] px-2.5 py-1 text-[12px] font-extrabold text-[#a5762e]" data-testid={`leader-badges-${r.rank}`}>
                    {r.badgeCount}/{totalSeasons}
                  </span>
                  <BadgeRow badges={r.badges} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
